import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  type Auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
} from 'firebase/auth';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

const DEFAULT_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyC83EmSurn1n_rjlga-1N-DBXtOw20d-RA',
  authDomain: 'farmspace-auth.firebaseapp.com',
  projectId: 'farmspace-auth',
  storageBucket: 'farmspace-auth.firebasestorage.app',
  messagingSenderId: '301601460213',
  appId: '1:301601460213:web:4e2afd76853806887cdabc',
};

function buildFirebaseConfig() {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY;
  const authDomain = import.meta.env.VITE_FIREBASE_AUTH_DOMAIN;
  const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
  const storageBucket = import.meta.env.VITE_FIREBASE_STORAGE_BUCKET;
  const messagingSenderId = import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID;
  const appId = import.meta.env.VITE_FIREBASE_APP_ID;
  const measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID;

  if (apiKey && authDomain && projectId) {
    return {
      apiKey,
      authDomain,
      projectId,
      storageBucket,
      messagingSenderId,
      appId,
      measurementId,
    };
  }

  return DEFAULT_FIREBASE_CONFIG;
}

export function hasFirebaseConfig() {
  const config = buildFirebaseConfig();
  return Boolean(config?.apiKey && config?.authDomain && config?.projectId);
}

export function getFirebaseApp(): FirebaseApp | null {
  if (_app) return _app;
  if (!hasFirebaseConfig()) return null;

  _app = initializeApp(buildFirebaseConfig());
  return _app;
}

export function getFirebaseAuth(): Auth | null {
  if (_auth) return _auth;
  const app = getFirebaseApp();
  if (!app) return null;
  _auth = getAuth(app);
  return _auth;
}

export async function firebaseEmailPasswordLogin(email: string, password: string): Promise<string> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('firebase_not_configured');
  const cred = await signInWithEmailAndPassword(auth, email, password);
  const token = await cred.user.getIdToken(true);
  return token;
}

export async function firebaseGoogleLogin(): Promise<{ token?: string; redirected?: boolean }> {
  const auth = getFirebaseAuth();
  if (!auth) throw new Error('firebase_not_configured');
  const provider = new GoogleAuthProvider();
  try {
    const cred = await signInWithPopup(auth, provider);
    const token = await cred.user.getIdToken(true);
    return { token };
  } catch (err: any) {
    const code = err?.code || err?.message || '';
    if (String(code).includes('auth/popup-blocked') || String(code).includes('auth/popup-closed-by-user')) {
      try {
        localStorage.setItem('dc.google_login_pending', '1');
      } catch {
        // ignore
      }
      await signInWithRedirect(auth, provider);
      return { redirected: true };
    }
    throw err;
  }
}

export async function firebaseGoogleRedirectResult(): Promise<string | null> {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  const result = await getRedirectResult(auth);
  if (!result?.user) return null;
  const token = await result.user.getIdToken(true);
  try {
    localStorage.removeItem('dc.google_login_pending');
  } catch {
    // ignore
  }
  return token;
}

export async function firebaseGetCurrentUserToken(timeoutMs = 2000): Promise<string | null> {
  const auth = getFirebaseAuth();
  if (!auth) return null;
  if (auth.currentUser) {
    return auth.currentUser.getIdToken(true);
  }

  return await new Promise((resolve) => {
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (settled || !user) return;
      settled = true;
      clearTimeout(timeout);
      unsubscribe();
      try {
        const token = await user.getIdToken(true);
        resolve(token);
      } catch {
        resolve(null);
      }
    });
  });
}

export async function firebaseLogout(): Promise<void> {
  const auth = getFirebaseAuth();
  if (!auth) return;
  await firebaseSignOut(auth);
}

